import React, { useState } from "react";
import {
  Card,
  DatePicker,
  Switch,
  Button,
  Row,
  Col,
  Statistic,
  message,
  Spin
} from "antd";
import { CalendarOutlined, DollarOutlined } from "@ant-design/icons";
import moment from "moment";
import { ConfigProvider } from "antd";
import enUS from "antd/es/locale/en_US";
import facturaApi from "../../facturas/store/api";
import AdminBreadCrumb from "components/AdminBreadCrumb";
import "./RevenueReport.scss";

const { RangePicker } = DatePicker;

// Create custom locale with Monday as first day of week
const mondayFirstLocale = {
  ...enUS,
  lang: {
    ...enUS.lang,
    weekStart: 1
  },
  DatePicker: {
    ...enUS.DatePicker,
    lang: {
      ...enUS.DatePicker.lang,
      weekStart: 1
    }
  }
};

// Custom RangePicker component with Monday-first week
const MondayFirstRangePicker = (props) => {
  // Create a temporary moment locale for this component
  const tempLocale = moment.localeData()._config;

  React.useEffect(() => {
    // Temporarily set Monday as first day
    moment.updateLocale("en", {
      week: {
        dow: 1, // Monday
        doy: 4
      }
    });

    return () => {
      // Restore original locale on cleanup
      moment.updateLocale("en", tempLocale);
    };
  }, []);

  return (
    <ConfigProvider locale={mondayFirstLocale}>
      <RangePicker {...props} />
    </ConfigProvider>
  );
};

const RevenueReport = () => {
  const [dateRange, setDateRange] = useState([
    moment().startOf("isoWeek"),
    moment().endOf("isoWeek")
  ]);
  const [onlyPaidFacturas, setOnlyPaidFacturas] = useState(true);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  const breadCrumbs = [
    { text: "BILLING", url: "/admin/billing-dashboard" },
    { text: "REPORTS", url: "/admin/billing/reports" },
    { text: "REVENUE", url: "/admin/billing/reports/revenue" }
  ];

  const handleApply = async () => {
    if (!dateRange || dateRange.length !== 2) {
      message.error("Please select a valid date range");
      return;
    }

    setLoading(true);
    try {
      const params = {
        startDate: dateRange[0].format("YYYY-MM-DD"),
        endDate: dateRange[1].format("YYYY-MM-DD"),
        pageSize: 10000 // Get all records for calculation
      };

      if (onlyPaidFacturas) {
        params.status = "paid";
      }

      const response = await facturaApi.getFacturas(params);
      const facturas = response.data || [];

      // Calculate Managed vs SaaS vs Manual revenue
      let managedRevenue = 0;
      let saasRevenue = 0;
      let manualRevenue = 0;
      let managedCount = 0;
      let saasCount = 0;
      let manualCount = 0;

      facturas.forEach((factura) => {
        const amount = parseFloat(factura.total) || 0;
        const facturaType = factura.facturaType || "Manual";

        if (facturaType === "Managed") {
          managedRevenue += amount;
          managedCount++;
        } else if (facturaType === "SaaS") {
          saasRevenue += amount;
          saasCount++;
        } else {
          manualRevenue += amount;
          manualCount++;
        }
      });

      setReportData({
        managedRevenue,
        saasRevenue,
        manualRevenue,
        managedCount,
        saasCount,
        manualCount,
        totalRevenue: managedRevenue + saasRevenue + manualRevenue,
        totalCount: managedCount + saasCount + manualCount,
        dateRange: `${dateRange[0].format("MMM D, YYYY")} - ${dateRange[1].format(
          "MMM D, YYYY"
        )}`,
        onlyPaid: onlyPaidFacturas
      });
    } catch (error) {
      console.error("Error fetching report data:", error);
      message.error("Failed to generate report");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="revenue-report-page admin">
      <div className="common-header">
        <AdminBreadCrumb breadCrumbs={breadCrumbs} />
      </div>

      <div className="content-wrapper">
        <h2>Revenue Report</h2>
        <p style={{ marginBottom: 24, color: "#666" }}>
          SaaS revenue vs Delivery revenue
        </p>

        <Card title="Report Parameters" style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>Date Range</div>
              <MondayFirstRangePicker
                value={dateRange}
                onChange={setDateRange}
                format="YYYY-MM-DD"
                style={{ width: "100%" }}
              />
            </Col>

            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>Filter</div>
              <div style={{ display: "flex", alignItems: "center", height: 32 }}>
                <Switch
                  checked={onlyPaidFacturas}
                  onChange={setOnlyPaidFacturas}
                  style={{ marginRight: 8 }}
                />
                <span>Only paid facturas</span>
              </div>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: 8 }}>&nbsp;</div>
              <Button
                type="primary"
                icon={<CalendarOutlined />}
                onClick={handleApply}
                loading={loading}
                style={{ width: "100%" }}
              >
                Generate Report
              </Button>
            </Col>
          </Row>
        </Card>

        {reportData && (
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={8}>
              <Card
                title="Managed Revenue"
                style={{ height: "100%" }}
                headStyle={{
                  backgroundColor: "#e6f7ff",
                  borderBottom: "1px solid #91d5ff"
                }}
              >
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <Statistic
                      title="Total Revenue"
                      value={reportData.managedRevenue}
                      precision={2}
                      prefix={<DollarOutlined />}
                      valueStyle={{ color: "#1890ff" }}
                    />
                  </Col>
                  <Col span={24}>
                    <Statistic
                      title="Number of Invoices"
                      value={reportData.managedCount}
                      valueStyle={{ color: "#1890ff" }}
                    />
                  </Col>
                  <Col span={24}>
                    <div style={{ fontSize: 12, color: "#666" }}>Includes: Managed</div>
                  </Col>
                </Row>
              </Card>
            </Col>

            <Col xs={24} lg={8}>
              <Card
                title="SaaS Revenue"
                style={{ height: "100%" }}
                headStyle={{
                  backgroundColor: "#f6ffed",
                  borderBottom: "1px solid #b7eb8f"
                }}
              >
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <Statistic
                      title="Total Revenue"
                      value={reportData.saasRevenue}
                      precision={2}
                      prefix={<DollarOutlined />}
                      valueStyle={{ color: "#52c41a" }}
                    />
                  </Col>
                  <Col span={24}>
                    <Statistic
                      title="Number of Invoices"
                      value={reportData.saasCount}
                      valueStyle={{ color: "#52c41a" }}
                    />
                  </Col>
                  <Col span={24}>
                    <div style={{ fontSize: 12, color: "#666" }}>Includes: SaaS</div>
                  </Col>
                </Row>
              </Card>
            </Col>

            <Col xs={24} lg={8}>
              <Card
                title="Manual Revenue"
                style={{ height: "100%" }}
                headStyle={{
                  backgroundColor: "#fff7e6",
                  borderBottom: "1px solid #ffd591"
                }}
              >
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <Statistic
                      title="Total Revenue"
                      value={reportData.manualRevenue}
                      precision={2}
                      prefix={<DollarOutlined />}
                      valueStyle={{ color: "#fa8c16" }}
                    />
                  </Col>
                  <Col span={24}>
                    <Statistic
                      title="Number of Invoices"
                      value={reportData.manualCount}
                      valueStyle={{ color: "#fa8c16" }}
                    />
                  </Col>
                  <Col span={24}>
                    <div style={{ fontSize: 12, color: "#666" }}>Includes: Manual</div>
                  </Col>
                </Row>
              </Card>
            </Col>

            <Col span={24}>
              <Card title="Summary">
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={8}>
                    <Statistic
                      title="Total Revenue"
                      value={reportData.totalRevenue}
                      precision={2}
                      prefix={<DollarOutlined />}
                      valueStyle={{ color: "#722ed1" }}
                    />
                  </Col>
                  <Col xs={24} sm={8}>
                    <Statistic
                      title="Total Invoices"
                      value={reportData.totalCount}
                      valueStyle={{ color: "#722ed1" }}
                    />
                  </Col>
                  <Col xs={24} sm={8}>
                    <div style={{ fontSize: 14, color: "#666", paddingTop: 16 }}>
                      <strong>Period:</strong> {reportData.dateRange}
                      <br />
                      <strong>Filter:</strong>{" "}
                      {reportData.onlyPaid ? "Paid only" : "All statuses"}
                    </div>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        )}

        {loading && (
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <Spin size="large" />
            <div style={{ marginTop: 16, color: "#666" }}>Generating report...</div>
          </div>
        )}

        {!reportData && !loading && (
          <Card>
            <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>
              <CalendarOutlined style={{ fontSize: 48, marginBottom: 16 }} />
              <h3>Delivery vs SaaS Revenue Report</h3>
              <p>Select a date range and click Generate Report to view the data.</p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default RevenueReport;
